import { useCallback, useMemo } from 'react';

import { type fnGetFilteredProductLinks, type ProductLink } from '@atlassian/global-shortcuts';
import { RequestStatus } from '@atlassian/global-shortcuts/constants';
import type { ShortcutsConfig } from '@atlassian/global-side-navigation/types';

import type {
	InvitedAppShortcutAllowedTargetAppKey,
	ShortcutItemsConfig,
} from '../../../common/types';
import {
	getAppKeyByApsKey,
	isAllowedApsKey,
} from '../../../common/utils/invited-app-shortcuts-utils';

type ProductLinksByApsKey = Record<string, ProductLink[]>;
type ProductLinksByProduct = Partial<Record<InvitedAppShortcutAllowedTargetAppKey, ProductLink[]>>;

interface UseInvitedAppProductLinksArgs {
	getCachedProductLinks: () => ProductLinksByProduct;
	shortcutItemsConfig: ShortcutItemsConfig;
	updateHydratedProductLinks: (productLinks: ProductLinksByProduct) => void;
}

interface UseInvitedAppProductLinksResult {
	handleUpdateInvitedAppProductLinks: (getFilteredProductLinks: fnGetFilteredProductLinks) => void;
	handleUpdateProductLinksFromSettledShortcuts: (settledShortcuts: ShortcutsConfig) => void;
	invitedAppProductLinks: Record<string, ProductLink[]>;
}

export const useInvitedAppProductLinks = ({
	shortcutItemsConfig,
	getCachedProductLinks,
	updateHydratedProductLinks,
}: UseInvitedAppProductLinksArgs): UseInvitedAppProductLinksResult => {
	const invitedAppProductLinks = useMemo<Record<string, ProductLink[]>>(
		() => resolveCachedInvitedAppProductLinks(shortcutItemsConfig, getCachedProductLinks),
		[shortcutItemsConfig, getCachedProductLinks],
	);

	/**
	 * Update invited app product links when getFilteredProductLinks changes
	 */
	const handleUpdateInvitedAppProductLinks = useCallback(
		(getFilteredProductLinks: fnGetFilteredProductLinks): void => {
			if (typeof getFilteredProductLinks === 'function') {
				const hydratedLinks = resolveInvitedAppProductLinks(
					getFilteredProductLinks,
					shortcutItemsConfig,
				);
				if (Object.keys(hydratedLinks).length > 0) {
					updateHydratedProductLinks(toProductLinksByProduct(hydratedLinks));
				}
			}
		},
		[shortcutItemsConfig, updateHydratedProductLinks],
	);

	/**
	 * Persist the product links derived from GSN's settled shortcuts into the cookie cache, so the
	 * next visit can seed the shortcuts menu during SSR (the GSN itself does not cache product
	 * links). Mirrors `handleUpdateInvitedAppProductLinks`, but sources the links from the already
	 * hydrated shortcuts instead of a `getFilteredProductLinks` filter.
	 */
	const handleUpdateProductLinksFromSettledShortcuts = useCallback(
		(settled: ShortcutsConfig): void => {
			const resolved = resolveProductLinksFromSettledShortcuts(settled, shortcutItemsConfig);
			if (Object.keys(resolved).length > 0) {
				updateHydratedProductLinks(toProductLinksByProduct(resolved));
			}
		},
		[shortcutItemsConfig, updateHydratedProductLinks],
	);

	return {
		invitedAppProductLinks,
		handleUpdateInvitedAppProductLinks,
		handleUpdateProductLinksFromSettledShortcuts,
	};
};

/**
 * Derive product links from GSN's settled shortcuts, keyed by APS key.
 *
 * For each app the shortcuts menu is configured for, look up the matching settled shortcut and
 * turn its hydrated `url` into a single-element `ProductLink[]`. When the settled shortcut has no
 * hydrated URL (the user has no resolvable link for that app), an empty array is written; downstream
 * `toProductLinksByProduct` then drops those empty entries so no linkless product is persisted.
 */
const resolveProductLinksFromSettledShortcuts = (
	settledShortcuts: ShortcutsConfig,
	shortcutItemsConfig: ShortcutItemsConfig,
): ProductLinksByApsKey => {
	if (Object.keys(shortcutItemsConfig).length === 0) {
		return {};
	}

	// Map each settled shortcut's APS key to its hydrated URL.
	const urlByApsKey = new Map<string, string>();
	for (const shortcut of Object.values(settledShortcuts)) {
		if (shortcut.type === 'link' && shortcut.apsKey && shortcut.url) {
			urlByApsKey.set(shortcut.apsKey, shortcut.url);
		}
	}

	return Object.values(shortcutItemsConfig).reduce<ProductLinksByApsKey>((acc, config) => {
		if (config.type === 'link' && config.apsKey) {
			const url = urlByApsKey.get(config.apsKey);
			acc[config.apsKey] = url ? [{ url }] : [];
		}
		return acc;
	}, {});
};

const resolveInvitedAppProductLinks = (
	getFilteredProductLinks: fnGetFilteredProductLinks,
	shortcutItemsConfig: ShortcutItemsConfig,
) => {
	// No need to resolve links if there are no shortcut items config
	if (Object.keys(shortcutItemsConfig).length === 0) {
		return {};
	}

	let loaded = false;
	const resolved = Object.entries(shortcutItemsConfig).reduce<ProductLinksByApsKey>(
		(acc, [, config]) => {
			if (config.type === 'link' && config.apsKey) {
				const { links, status } = getFilteredProductLinks(config.apsKey);
				// the status is same across all links, so we can use it to determine if the links are loaded
				if (status === RequestStatus.COMPLETE) {
					loaded = true;
				}
				acc[config.apsKey] = links;
			}
			return acc;
		},
		{},
	);
	return loaded ? resolved : {};
};

const resolveCachedInvitedAppProductLinks = (
	shortcutItemsConfig: ShortcutItemsConfig,
	getCachedProductLinks: () => ProductLinksByProduct,
): ProductLinksByApsKey => {
	const cachedProductLinks = getCachedProductLinks();
	if (!cachedProductLinks) {
		return {};
	}

	return Object.values(shortcutItemsConfig).reduce<ProductLinksByApsKey>((acc, config) => {
		if (config.type === 'link' && config.apsKey && isAllowedApsKey(config.apsKey)) {
			const product = getAppKeyByApsKey(config.apsKey);
			const links = cachedProductLinks[product];
			if (links) {
				acc[config.apsKey] = links;
			}
		}
		return acc;
	}, {});
};

const toProductLinksByProduct = (
	productLinksByApsKey: ProductLinksByApsKey,
): ProductLinksByProduct =>
	Object.entries(productLinksByApsKey).reduce<ProductLinksByProduct>((acc, [apsKey, links]) => {
		// Only update when the links are not empty
		// This is to avoid overwriting the cached product links when shortcuts are not added to the shortcuts menu
		// A caveat is when the users lose access to the product, the cached links will not be updated until the next page
		if (isAllowedApsKey(apsKey) && links.length > 0) {
			const product = getAppKeyByApsKey(apsKey);
			acc[product] = links;
		}
		return acc;
	}, {});
